import httpMethod from 'services/update2025/httpMethod';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import BlockDetail from "components/block-detail/BlockDetail";
import { SmsLogContext } from "components/sms-log/SmsLogContext";
import { SmsLogEditor } from "components/sms-log/SmsLogEditor";
import moment from "moment";
import { findSmsLogs } from "services/SmsLogService";
import { X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import { checkDateMonth, dateFormat1, dateFormat2, range2 } from "utils/date";
import { Calendar } from "primereact/calendar";

export const SmsLogList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["sms-log", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const smsLogContext = useContext(SmsLogContext);
    const { smsLog, setSmsLog, smsLogs, setSmsLogs, setSmsLogDialog } = smsLogContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(true);

    const now = new Date();

    const beforeNow = new Date(new Date().setDate(new Date().getDate() - range2));

    const [formatDateVal, setFormatDateVal] = useState(dateFormat1);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            phoneNumber: { value: null, matchMode: "equals" },
            status: { value: null, matchMode: "equals" },
            fromDate: { value: beforeNow, matchMode: "gte" },
            toDate: { value: now, matchMode: "lte" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const logStatus = [
        { label: t("not_send"), value: "NOT_SEND" },
        { label: t("sent_success"), value: "SENT_SUCCESS" },
        { label: t("sent_failure"), value: "SENT_FAILURE" },
    ];

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);

        findSmsLogs(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setSmsLogs(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => {
                setLoading(false);
            });
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openDialog = (smsLog) => {
        setSmsLog({ ...smsLog });
        setSmsLogDialog(true);
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const logStatusFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={logStatus} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const logStatusBodyTemplate = (rowData) => {
        return (
            <div className="flex">
                <p>{t(String(rowData.status).toLowerCase())}</p>
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Số điện thoại */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="phoneNumber"
                    >
                        {t("smsLog.phoneNumber")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="phoneNumber"
                            name="phoneNumber"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        phoneNumber: {
                                            ...lazyParams.filters.phoneNumber,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* Ngày nhận từ */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "100px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="fromDate"
                    >
                        {t("smsLog.fromDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.fromDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        fromDate: {
                                            ...lazyParams.filters.fromDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="fromDate"
                            id="fromDate"
                            maxDate={lazyParams.filters.toDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                    </div>
                </div>
            </div>

            {/* đến */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="toDate"
                    >
                        {t("smsLog.toDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.toDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        toDate: {
                                            ...lazyParams.filters.toDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="toDate"
                            id="toDate"
                            // maxDate={now}
                            minDate={lazyParams.filters.fromDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                    </div>
                </div>
            </div>

            {/* trạng thái */}
            <div className="col-12 md:col-2">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "85px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="status"
                    >
                        {t("status")}
                    </label>
                    <Dropdown
                        value={lazyParams?.filters?.status?.value}
                        className="col"
                        style={{
                            width: "100%",
                            // width: "calc(100% - 100px)",
                            margin: "5px 0px 0px 0px",
                        }}
                        showClear={true}
                        id="status"
                        name="status"
                        options={logStatus}
                        onChange={(e) => {
                            setCheckLoad(false);
                            setLazyParams({
                                ...lazyParams,
                                filters: {
                                    ...lazyParams.filters,
                                    status: {
                                        ...lazyParams.filters.status,
                                        value: e.value,
                                    },
                                },
                            });
                        }}
                    ></Dropdown>
                </div>
            </div>

            <div className="col-12 md:col-1 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                if (lazyParams.filters.phoneNumber.value) {
                                    setCheckLoad(true);
                                    setLazyParams({
                                        ...lazyParams,
                                        first: 0,
                                        page: 0,
                                    });
                                } else {
                                    if (lazyParams.filters.fromDate.value && lazyParams.filters.toDate.value) {
                                        if (checkDateMonth(lazyParams.filters.fromDate.value, lazyParams.filters.toDate.value, range2)) {
                                            setCheckLoad(true);
                                            setLazyParams({
                                                ...lazyParams,
                                                first: 0,
                                                page: 0,
                                            });
                                        } else {
                                            toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("smsLog.rangeFromToDate"), life: 3000 });
                                        }
                                    } else {
                                        toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("smsLog.requiredfromandtodate"), life: 3000 });
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const formatDateInput = (data) => {
        if (data) {
            const year = data?.substr(0, 4);
            const month = data?.substr(5, 2);
            const day = data?.substr(8, 2);
            const hour = data?.substr(11, 2);
            const minute = data?.substr(14, 2);
            const second = data?.substr(17, 2);

            return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
        }
        return null;
    };

    const createdDateBodyTemplate = (rowData) => {
        if (rowData?.createdDate) {
            return (
                <div className="flex">
                    <p>{formatDateInput(rowData.createdDate)}</p>
                </div>
            );
        } else {
            return null;
        }
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("smsLog.list")}</h5>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openDialog(rowData)} />
            </div>
        );
    };

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("smsLog.list")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("smsLog.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={smsLogs}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column
                                        field="phoneNumber"
                                        header={t("phoneNumber")}
                                        sortable
                                        // filter
                                        // showFilterMatchModes={false}
                                        filterElement={textFilterTemplate}
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column
                                        field="status"
                                        header={t("status")}
                                        // filterElement={logStatusFilterTemplate}
                                        body={logStatusBodyTemplate}
                                        sortable
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column
                                        field={"createdDate"}
                                        header={t("createdDate")}
                                        sortable
                                        filterElement={textFilterTemplate}
                                        // body={createdDateBodyTemplate}
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>

                    <SmsLogEditor />
                </div>
            </div>
        </div>
    );
};
